import React, { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { PartnerDropdown } from './PartnerDropdown';

/**
 * PayPayoutModal - used when admin clicks "Pay" on a pending payout.
 * Two modes:
 *   1) INR  → needs partner_id (partner whose cash pool pays the client)
 *   2) USDT_ADHOC → INR client paid in USDT at a company-decided rate; captures
 *      FX variance vs cycle rate into settlement_adjustments.
 */
export const PayPayoutModal = ({ payout, onClose, onPaid }) => {
  const [mode, setMode] = useState('INR');
  const [partnerId, setPartnerId] = useState('');
  const [adhocRate, setAdhocRate] = useState('');
  const [sourceExchange, setSourceExchange] = useState('');
  const [exchanges, setExchanges] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axiosInstance.get('/exchanges').then((r) => setExchanges(r.data || [])).catch(() => {});
  }, []);

  const isInrBase = (payout?.base_currency || 'INR') === 'INR';
  const referenceRate = payout?.conversion_rate || payout?.conversion_rate_for_usdt || 0;
  const profitBase = payout?.profit_amount || 0;  // in client's base currency

  // For INR-base + USDT_ADHOC: profit is INR, sent USDT = INR / rate
  const usdtSent = useMemo(() => {
    const r = parseFloat(adhocRate);
    if (!r || r <= 0 || !profitBase || !isInrBase) return 0;
    return profitBase / r;
  }, [adhocRate, profitBase, isInrBase]);

  // For USDT-base + INR_ADHOC: profit is USDT, sent INR = USDT * rate
  const inrSent = useMemo(() => {
    const r = parseFloat(adhocRate);
    if (!r || r <= 0 || !profitBase || isInrBase) return 0;
    return profitBase * r;
  }, [adhocRate, profitBase, isInrBase]);

  const usdtAtReference = useMemo(() => {
    if (!referenceRate || !profitBase || !isInrBase) return 0;
    return profitBase / referenceRate;
  }, [referenceRate, profitBase, isInrBase]);
  const usdtVariance = useMemo(() => usdtAtReference - usdtSent, [usdtAtReference, usdtSent]);

  const inrAtReference = useMemo(() => {
    if (!referenceRate || !profitBase || isInrBase) return 0;
    return profitBase * referenceRate;
  }, [referenceRate, profitBase, isInrBase]);
  const inrVariance = useMemo(() => inrSent - inrAtReference, [inrSent, inrAtReference]);

  // Auto-select correct default mode based on client base currency.
  useEffect(() => {
    if (!isInrBase) setMode('USDT');  // USDT-base client default = pay in USDT from exchange
  }, [isInrBase]);

  if (!payout) return null;

  const submit = async () => {
    if (mode === 'INR' && !partnerId) {
      toast.error('Select a partner');
      return;
    }
    if (mode === 'USDT_ADHOC') {
      if (!adhocRate || parseFloat(adhocRate) <= 0) {
        toast.error('Enter a positive adhoc rate');
        return;
      }
      if (!sourceExchange) {
        toast.error('Select source exchange');
        return;
      }
    }
    if (mode === 'USDT' && !sourceExchange) {
      toast.error('Select source exchange');
      return;
    }
    if (mode === 'INR_ADHOC') {
      if (!adhocRate || parseFloat(adhocRate) <= 0) {
        toast.error('Enter a positive adhoc rate');
        return;
      }
      if (!partnerId) {
        toast.error('Select the partner whose cash pool pays this INR');
        return;
      }
    }

    setSubmitting(true);
    try {
      let body;
      if (mode === 'INR') {
        body = { mode: 'INR', partner_id: partnerId, notes };
      } else if (mode === 'USDT_ADHOC') {
        body = { mode: 'USDT_ADHOC', adhoc_rate: parseFloat(adhocRate), source_exchange: sourceExchange, notes };
      } else if (mode === 'USDT') {
        body = { mode: 'USDT', source_exchange: sourceExchange, notes };
      } else {
        body = { mode: 'INR_ADHOC', adhoc_rate: parseFloat(adhocRate), partner_id: partnerId, notes };
      }
      const { data } = await axiosInstance.post(`/payouts/${payout.id}/pay`, body);
      toast.success(data.message || 'Payout paid');
      onPaid && onPaid();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pay payout');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl">
        <div className="flex justify-between items-center p-5 border-b">
          <div>
            <h2 className="text-base font-bold">Pay: {payout.client_code} — {payout.client_name}</h2>
            <p className="text-xs text-gray-500">
              Profit: {isInrBase ? `₹${profitBase.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `${profitBase.toLocaleString()} USDT`}
              {referenceRate > 0 && ` · Cycle rate: ₹${referenceRate}/USDT`}
            </p>
          </div>
          <button onClick={onClose} data-testid="pay-modal-close" className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Mode toggle — different choices per base currency */}
          {isInrBase ? (
            <div className="flex gap-2">
              <button
                onClick={() => setMode('INR')}
                data-testid="pay-mode-inr"
                className={`flex-1 px-3 py-2 rounded border text-sm font-medium ${mode === 'INR' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
              >
                Pay in INR
              </button>
              <button
                onClick={() => setMode('USDT_ADHOC')}
                data-testid="pay-mode-usdt-adhoc"
                className={`flex-1 px-3 py-2 rounded border text-sm font-medium ${mode === 'USDT_ADHOC' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
              >
                Pay in USDT (adhoc rate)
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setMode('USDT')}
                data-testid="pay-mode-usdt"
                className={`flex-1 px-3 py-2 rounded border text-sm font-medium ${mode === 'USDT' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
              >
                Pay in USDT
              </button>
              <button
                onClick={() => setMode('INR_ADHOC')}
                data-testid="pay-mode-inr-adhoc"
                className={`flex-1 px-3 py-2 rounded border text-sm font-medium ${mode === 'INR_ADHOC' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
              >
                Pay in INR (adhoc rate)
              </button>
            </div>
          )}

          {/* USDT flow (USDT clients) */}
          {mode === 'USDT' && !isInrBase && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Source Exchange <span className="text-red-500">*</span></label>
              <select
                value={sourceExchange}
                onChange={(e) => setSourceExchange(e.target.value)}
                data-testid="pay-source-exchange-usdt"
                className="form-input w-full text-sm"
              >
                <option value="">Select exchange…</option>
                {exchanges.map((ex) => (
                  <option key={ex.id} value={ex.name}>{ex.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* INR flow (INR clients only) */}
          {mode === 'INR' && isInrBase && (
            <PartnerDropdown
              value={partnerId}
              onChange={setPartnerId}
              required
              label="Paid from partner"
              testId="pay-partner-select"
            />
          )}

          {/* USDT adhoc flow (INR clients) */}
          {mode === 'USDT_ADHOC' && isInrBase && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Adhoc Rate (INR per 1 USDT) <span className="text-red-500">*</span></label>
                <input
                  type="number" step="0.01"
                  value={adhocRate}
                  onChange={(e) => setAdhocRate(e.target.value)}
                  placeholder={referenceRate ? `Cycle rate was ₹${referenceRate}` : 'e.g., 85'}
                  data-testid="pay-adhoc-rate"
                  className="form-input w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Source Exchange <span className="text-red-500">*</span></label>
                <select
                  value={sourceExchange}
                  onChange={(e) => setSourceExchange(e.target.value)}
                  data-testid="pay-source-exchange"
                  className="form-input w-full text-sm"
                >
                  <option value="">Select exchange…</option>
                  {exchanges.map((ex) => (
                    <option key={ex.id} value={ex.name}>{ex.name}</option>
                  ))}
                </select>
              </div>
              {/* Live preview */}
              {adhocRate && parseFloat(adhocRate) > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm space-y-1">
                  <div>USDT to send: <strong>{usdtSent.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT</strong></div>
                  {referenceRate > 0 && (
                    <div className={`flex items-center gap-1 ${usdtVariance > 0.01 ? 'text-green-700' : usdtVariance < -0.01 ? 'text-red-700' : 'text-gray-700'}`}>
                      {usdtVariance > 0.01 && <Check className="w-4 h-4" />}
                      {usdtVariance < -0.01 && <AlertTriangle className="w-4 h-4" />}
                      FX variance vs cycle rate:
                      <strong>
                        {usdtVariance >= 0 ? '+' : ''}{usdtVariance.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT
                      </strong>
                      <span className="text-xs text-gray-500">
                        ({usdtVariance > 0.01 ? 'company gain' : usdtVariance < -0.01 ? 'company loss' : 'neutral'})
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* INR adhoc flow (USDT clients paid INR cash) */}
          {mode === 'INR_ADHOC' && !isInrBase && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Adhoc Rate (INR per 1 USDT) <span className="text-red-500">*</span></label>
                <input
                  type="number" step="0.01"
                  value={adhocRate}
                  onChange={(e) => setAdhocRate(e.target.value)}
                  placeholder={referenceRate ? `Cycle rate was ₹${referenceRate}` : 'e.g., 95'}
                  data-testid="pay-adhoc-rate-inr"
                  className="form-input w-full text-sm"
                />
              </div>
              <PartnerDropdown
                value={partnerId}
                onChange={setPartnerId}
                required
                label="Paid from partner (INR cash pool)"
                testId="pay-partner-select-inr-adhoc"
              />
              {/* Live preview */}
              {adhocRate && parseFloat(adhocRate) > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm space-y-1">
                  <div>INR to send: <strong>₹{inrSent.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></div>
                  {referenceRate > 0 && (
                    <div className={`flex items-center gap-1 ${inrVariance < -0.99 ? 'text-green-700' : inrVariance > 0.99 ? 'text-red-700' : 'text-gray-700'}`}>
                      {inrVariance < -0.99 && <Check className="w-4 h-4" />}
                      {inrVariance > 0.99 && <AlertTriangle className="w-4 h-4" />}
                      FX variance vs cycle rate:
                      <strong>
                        {inrVariance >= 0 ? '+' : ''}₹{inrVariance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </strong>
                      <span className="text-xs text-gray-500">
                        ({inrVariance < -0.99 ? 'company gain' : inrVariance > 0.99 ? 'company loss' : 'neutral'})
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reference number, transaction hash, etc."
              data-testid="pay-notes"
              className="form-input w-full text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="pay-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="pay-submit"
            className="btn-primary text-sm flex items-center gap-1"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
};
